import { api } from "../../../api/Middleware";
import {
  PUBLICATIONS_URL,
  PUBLICATIONS_STATUS_SUMMARY_URL,
  PUBLICATIONS_PUBLISH_URL,
  PUBLICATIONS_REJECT_URL,
  PUBLICATIONS_SUSPEND_URL,
  PUBLICATIONS_REMOVE_URL,
} from "../../../api/Urls";

// =============================
// PUBLICATIONS SERVICES
// =============================

class PublicationsServices {
  // Listar todas as publicações
  static async listPublications(params = {}) {
    try {
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
        
      const url = queryString ? `${PUBLICATIONS_URL}?${queryString}` : PUBLICATIONS_URL;
      return await api.get(url);
    } catch (error) {
      console.error('Erro ao buscar publicações:', error);
      throw error;
    }
  }

  // Buscar publicação específica
  static async getPublication(id) {
    try {
      return await api.get(`${PUBLICATIONS_URL}${id}/`);
    } catch (error) {
      console.error('Erro ao buscar publicação:', error);
      throw error;
    }
  }

  // Criar nova publicação
  static async createPublication(data) {
    try {
      return await api.post(PUBLICATIONS_URL, data);
    } catch (error) {
      console.error('Erro ao criar publicação:', error);
      throw error;
    }
  }

  // Atualizar publicação existente
  static async updatePublication(id, data) {
    try {
      return await api.put(`${PUBLICATIONS_URL}${id}/`, data);
    } catch (error) {
      console.error('Erro ao atualizar publicação:', error);
      throw error;
    }
  }

  // Atualizar parcialmente publicação
  static async patchPublication(id, data) {
    try {
      return await api.patch(`${PUBLICATIONS_URL}${id}/`, data);
    } catch (error) {
      console.error('Erro ao atualizar publicação:', error);
      throw error;
    }
  }

  // Deletar publicação
  static async deletePublication(id) {
    try {
      return await api.delete(`${PUBLICATIONS_URL}${id}/`);
    } catch (error) {
      console.error('Erro ao deletar publicação:', error);
      throw error;
    }
  }

  // Obter resumo de status das publicações
  static async getStatusSummary() {
    try {
      return await api.get(PUBLICATIONS_STATUS_SUMMARY_URL);
    } catch (error) {
      console.error('Erro ao buscar resumo de status:', error);
      throw error;
    }
  }

  // Publicar agente (marcar como publicado)
  static async publishPublication(id) {
    try {
      return await api.post(PUBLICATIONS_PUBLISH_URL(id));
    } catch (error) {
      console.error('Erro ao publicar:', error);
      throw error;
    }
  }

  // Rejeitar publicação
  static async rejectPublication(id, reason = '') {
    try {
      const data = reason ? { reason } : {};
      return await api.post(PUBLICATIONS_REJECT_URL(id), data);
    } catch (error) {
      console.error('Erro ao rejeitar publicação:', error);
      throw error;
    }
  }

  // Suspender publicação
  static async suspendPublication(id) {
    try {
      return await api.post(PUBLICATIONS_SUSPEND_URL(id));
    } catch (error) {
      console.error('Erro ao suspender publicação:', error);
      throw error;
    }
  }

  // Remover publicação do marketplace
  static async removePublication(id) {
    try {
      return await api.post(PUBLICATIONS_REMOVE_URL(id));
    } catch (error) {
      console.error('Erro ao remover publicação:', error);
      throw error;
    }
  }

  // =============================
  // HELPER FUNCTIONS
  // =============================

  // Função auxiliar para construir parâmetros de busca
  static buildSearchParams(filters = {}) {
    const params = {};
    
    // Filtros básicos
    if (filters.status && filters.status !== 'all') {
      params.status = filters.status;
    }
    
    if (filters.search && filters.search.trim()) {
      params.search = filters.search.trim();
    }
    
    // Paginação
    if (filters.page) {
      params.page = filters.page;
    }
    
    if (filters.page_size) {
      params.page_size = filters.page_size;
    }
    
    return params;
  }

  // Função auxiliar para formatar dados da publicação
  static formatPublicationData(publication) {
    if (!publication) return null;

    return {
      ...publication,
      formattedCreatedAt: publication.created_at 
        ? new Date(publication.created_at).toLocaleDateString('pt-BR')
        : '',
      formattedUpdatedAt: publication.updated_at
        ? new Date(publication.updated_at).toLocaleDateString('pt-BR')
        : '',
      formattedPublishedAt: publication.published_at
        ? new Date(publication.published_at).toLocaleDateString('pt-BR')
        : null,
      statusVariant: this.getStatusVariant(publication.status),
      statusText: this.getStatusText(publication.status),
      canPublish: publication.status === 'pending',
      canReject: publication.status === 'pending',
      canSuspend: publication.status === 'published',
      canRemove: ['published', 'suspended'].includes(publication.status),
      canEdit: !['removed', 'rejected'].includes(publication.status),
    };
  }

  // Função auxiliar para obter variante de cor do status
  static getStatusVariant(status) {
    const variants = {
      published: "success",
      pending: "primary", 
      suspended: "warning",
      removed: "danger",
      rejected: "secondary"
    };
    return variants[status] || "secondary";
  }

  // Função auxiliar para obter texto do status
  static getStatusText(status) {
    const texts = {
      published: "Publicado",
      pending: "Pendente",
      suspended: "Suspenso", 
      removed: "Removido",
      rejected: "Rejeitado"
    };
    return texts[status] || status;
  }

  // Validar dados para criação de publicação
  static validatePublicationData(data) {
    const errors = [];
    
    if (!data.agent) {
      errors.push("ID do agente é obrigatório");
    }
    
    if (!data.marketplace_product_id || !data.marketplace_product_id.trim()) {
      errors.push("ID do produto no marketplace é obrigatório");
    }
    
    // Validar formato do ID do marketplace (exemplo: AGENT-001)
    if (data.marketplace_product_id && !/^[A-Z]+-\d+$/.test(data.marketplace_product_id.trim())) {
      errors.push("ID do marketplace deve seguir o formato: PREFIXO-NÚMERO (ex: AGENT-001)");
    }
    
    // Validar URL do marketplace se fornecida
    if (data.marketplace_url && data.marketplace_url.trim()) {
      try {
        new URL(data.marketplace_url.trim());
      } catch (e) {
        errors.push("URL do marketplace inválida");
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Preparar dados para criação de publicação
  static preparePublicationData(formData) {
    const data = {
      agent: formData.agent,
      marketplace_product_id: formData.marketplace_product_id?.trim(),
    };
    
    if (formData.marketplace_url && formData.marketplace_url.trim()) {
      data.marketplace_url = formData.marketplace_url.trim();
    }
    
    return data;
  }

  // Função para obter estatísticas das publicações
  static async getPublicationStats() {
    try {
      const [summaryResponse, publicationsResponse] = await Promise.all([
        this.getStatusSummary(),
        this.listPublications({ page_size: 1 })
      ]);
      
      const summary = summaryResponse.data;
      const total = publicationsResponse.data.count || 0;
      
      // Calcular percentuais
      const percentages = {};
      Object.keys(summary).forEach(status => {
        if (status !== 'total' && total > 0) {
          percentages[status] = Math.round((summary[status] / total) * 100);
        } else if (status !== 'total') {
          percentages[status] = 0;
        }
      });
      
      return {
        summary,
        total,
        percentages,
        healthScore: this.calculateHealthScore(summary),
      };
    } catch (error) {
      console.error('Erro ao obter estatísticas:', error);
      throw error;
    }
  }

  // Calcular score de saúde das publicações
  static calculateHealthScore(summary) {
    const { published = 0, pending = 0, suspended = 0, removed = 0 } = summary;
    const total = published + pending + suspended + removed;
    
    if (total === 0) return 0;
    
    // Score baseado na proporção de publicações ativas vs problemáticas
    const activeScore = (published / total) * 70; // 70% do score para publicadas
    const pendingScore = (pending / total) * 20; // 20% do score para pendentes
    const problemScore = ((suspended + removed) / total) * -10; // -10% para problemáticas
    
    return Math.max(0, Math.min(100, Math.round(activeScore + pendingScore + problemScore)));
  }

  // Função para buscar publicações com filtros avançados
  static async searchPublications(searchOptions = {}) {
    try {
      const {
        query = '',
        status = 'all',
        dateFrom = null,
        dateTo = null,
        sortBy = 'created_at',
        sortOrder = 'desc',
        page = 1,
        pageSize = 10
      } = searchOptions;

      const params = {
        page,
        page_size: pageSize,
      };

      if (query.trim()) {
        params.search = query.trim();
      }

      if (status && status !== 'all') {
        params.status = status;
      }

      if (dateFrom) {
        params.created_after = dateFrom;
      }

      if (dateTo) {
        params.created_before = dateTo;
      }

      if (sortBy) {
        params.ordering = sortOrder === 'desc' ? `-${sortBy}` : sortBy;
      }

      return await this.listPublications(params);
    } catch (error) {
      console.error('Erro na busca avançada:', error);
      throw error;
    }
  }

  // Função para exportar dados das publicações
  static async exportPublications(format = 'json', filters = {}) {
    try {
      const params = {
        ...this.buildSearchParams(filters),
        export_format: format,
        page_size: 1000, // Limite alto para exportação
      };

      const response = await this.listPublications(params);
      const data = response.data.results || [];

      if (format === 'csv') {
        return this.convertToCSV(data);
      }

      return data;
    } catch (error) {
      console.error('Erro ao exportar publicações:', error);
      throw error;
    }
  }

  // Converter dados para CSV
  static convertToCSV(data) {
    if (!data || data.length === 0) {
      return '';
    }

    const headers = [
      'ID',
      'Nome do Agente',
      'Área do Agente',
      'ID do Marketplace',
      'URL do Marketplace',
      'Status',
      'Publicado em',
      'Motivo da Rejeição',
      'Criado em',
      'Atualizado em'
    ];

    const rows = data.map(pub => [
      pub.id,
      pub.agent_name,
      pub.agent_area,
      pub.marketplace_product_id,
      pub.marketplace_url || '',
      pub.status_display,
      pub.published_at ? new Date(pub.published_at).toLocaleDateString('pt-BR') : '',
      pub.rejection_reason || '',
      new Date(pub.created_at).toLocaleDateString('pt-BR'),
      new Date(pub.updated_at).toLocaleDateString('pt-BR')
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csvContent;
  }

  // Download do arquivo CSV
  static downloadCSV(csvContent, filename = 'publicacoes.csv') {
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

export default PublicationsServices;